import { useCallback, useEffect, useRef, useState } from 'react';

import type { PageGraph, PageRead, PageSummary, RecentChange } from '@/api/client';
import { WikiApiError, wikiApiClient } from '@/api/client';
import { ASSETS, LIMITS, SITE } from '@/config';
import { RecentChanges } from '@/features/activity/RecentChanges';
import { Stats } from '@/features/activity/Stats';
import { UserAdmin } from '@/features/admin/UserAdmin';
import { useAuthContext } from '@/features/auth/AuthContext';
import { ChangePasswordPage, LoginPage, RegisterPage } from '@/features/auth/AuthPages';
import { AuthPanel } from '@/features/auth/AuthPanel';
import { AllPages } from '@/features/browse/AllPages';
import { CategoryIndex } from '@/features/categories/CategoryIndex';
import { CategoryView } from '@/features/categories/CategoryView';
import { SiteNav } from '@/features/chrome/SiteNav';
import { TopBar } from '@/features/chrome/TopBar';
import { GraphPanel } from '@/features/graph/GraphPanel';
import { RevisionHistory } from '@/features/history/RevisionHistory';
import { MapPreview } from '@/features/map/MapPreview';
import { MapView } from '@/features/map/MapView';
import { AuditLog } from '@/features/moderation/AuditLog';
import { ModerationDashboard } from '@/features/moderation/ModerationDashboard';
import { hasModeratorScope, hasSuperadminRole } from '@/features/moderation/permissions';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { ProposeEditor } from '@/features/propose/ProposeEditor';
import { usePendingProposals } from '@/features/propose/pendingProposals';
import { PageInfoView } from '@/features/reader/PageInfo';
import { ReaderArticle } from '@/features/reader/ReaderArticle';
import { TableOfContents } from '@/features/reader/TableOfContents';
import { Reports } from '@/features/reports/Reports';
import { PageListSidebar } from '@/features/search/PageListSidebar';
import { WatchedPages } from '@/features/watch/WatchedPages';
import { WatchToggle } from '@/features/watch/WatchToggle';
import { useLocale, useT } from '@/i18n/LocaleProvider';

import { buildPath, defaultLang, matchRoute, type Route } from './routes';

function AppShell() {
  const t = useT();
  const { setLocale } = useLocale();
  const initialRoute = readCurrentRoute();
  const auth = useAuthContext();
  const { proposals: pendingProposals, rememberProposal } = usePendingProposals(auth.account?.id);
  const [route, setRoute] = useState<Route>(() => initialRoute);
  const [activeSlug, setActiveSlug] = useState<string | null>(() => routeSlug(initialRoute));
  const [contentLang, setContentLang] = useState<string>(() => routeLang(initialRoute));
  const [recentPages, setRecentPages] = useState<PageSummary[]>([]);
  const [activePage, setActivePage] = useState<PageRead | null>(null);
  const [readerError, setReaderError] = useState<string | null>(null);
  const [pageMissing, setPageMissing] = useState<{ slug: string; lang: string } | null>(null);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PageSummary[] | null>(null);
  const [pageGraph, setPageGraph] = useState<PageGraph | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [isLoadingRecent, setIsLoadingRecent] = useState(true);
  const [isLoadingPage, setIsLoadingPage] = useState(false);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showGraph, setShowGraph] = useState(false);
  const [mainPageSlug, setMainPageSlug] = useState<string>('');
  const [mainPages, setMainPages] = useState<Record<string, string>>({});
  const redirectNoRef = useRef(false);

  // The home page is per content language (e.g. /en/ → the English main page),
  // falling back to the single configured slug when a language's home is unknown.
  const homeSlug = mainPages[contentLang] ?? mainPageSlug;
  const sidebarPages = query.trim() && searchResults ? searchResults : recentPages;
  // Suppress account until token validation resolves to avoid a flash of stale auth state.
  const validatedAccount = auth.status !== 'loading' ? auth.account : null;
  const canModerate = hasModeratorScope(validatedAccount);
  const canSuperadmin = hasSuperadminRole(validatedAccount);
  const relationsLabel = t('nav_relations');
  const graphToggleLabel = pageGraph
    ? `${relationsLabel} ${pageGraph.center.outbound_count}/${pageGraph.center.inbound_count}`
    : relationsLabel;

  const navigateToRoute = useCallback(
    (nextRoute: Route) => {
      const nextPath = buildPath(nextRoute, homeSlug, contentLang);

      if (currentLocationPath() !== nextPath) {
        window.history.pushState(null, '', nextPath);
      }

      setRoute(nextRoute);

      const nextSlug = routeSlug(nextRoute);
      if (nextSlug) {
        setActiveSlug(nextSlug);
      }

      // Only routes that carry their own language change contentLang; routes built
      // without one (login, stats, …) preserve the current language.
      if (nextRoute.lang) {
        setContentLang(nextRoute.lang);
      }
    },
    [homeSlug, contentLang],
  );

  const navigateTo = useCallback(
    (slug: string) => {
      const nextRoute: Route = { kind: 'reader', slug, lang: contentLang };
      const nextPath = buildPath(nextRoute, homeSlug, contentLang);

      if (currentLocationPath() !== nextPath) {
        window.history.pushState(null, '', nextPath);
      }

      setRoute(nextRoute);
      setActiveSlug(slug);
    },
    [homeSlug, contentLang],
  );

  const navigateHome = useCallback(() => {
    if (homeSlug) {
      navigateTo(homeSlug);
      return;
    }

    navigateToRoute({ kind: 'reader', slug: null, lang: contentLang });
  }, [homeSlug, contentLang, navigateTo, navigateToRoute]);

  const navigateRandomPage = useCallback(() => {
    wikiApiClient.randomPage(contentLang).then((page) => navigateTo(page.slug));
  }, [navigateTo, contentLang]);

  const navigateLang = useCallback(
    (lang: string, slug: string) => {
      const nextRoute: Route = { kind: 'reader', slug, lang };
      const nextPath = buildPath(nextRoute, homeSlug, lang);

      if (currentLocationPath() !== nextPath) {
        window.history.pushState(null, '', nextPath);
      }

      setRoute(nextRoute);
      setActiveSlug(slug);
      setContentLang(lang);
    },
    [homeSlug],
  );

  const navigateNoRedirect = useCallback(
    (slug: string) => {
      redirectNoRef.current = true;
      navigateTo(slug);
    },
    [navigateTo],
  );

  // selectLanguage switches BOTH the UI chrome locale and the content language,
  // then navigates to the current page's version in the chosen language: an existing
  // translation is opened directly; a missing one opens the "create translation"
  // proposal (carrying the shared translation key); otherwise it lands on that
  // language's home.
  const selectLanguage = useCallback(
    (code: string) => {
      setLocale(code);

      if (route.kind === 'reader' && activePage) {
        const translation = activePage.translations?.find((entry) => entry.language === code);
        if (translation) {
          navigateLang(code, translation.slug);
          return;
        }

        if (activePage.translation_group) {
          setContentLang(code);
          navigateToRoute({
            kind: 'newProposal',
            language: code,
            translationGroup: activePage.translation_group,
            title: activePage.title,
          });
          return;
        }
      }

      setContentLang(code);
      const languageHome = mainPages[code];
      if (languageHome) {
        navigateLang(code, languageHome);
        return;
      }

      navigateToRoute({ kind: 'reader', slug: null, lang: code });
    },
    [route, activePage, mainPages, navigateLang, navigateToRoute, setLocale],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadRecentPages() {
      setIsLoadingRecent(true);

      try {
        const result = await wikiApiClient.getRecentChanges({
          limit: LIMITS.sidebarRecentLimit,
          lang: contentLang,
        });

        if (cancelled) {
          return;
        }

        setRecentPages(result.changes.map(recentChangeToPageSummary));
      } catch {
        // sidebar recent list is best-effort; fail silently
      } finally {
        if (!cancelled) {
          setIsLoadingRecent(false);
        }
      }
    }

    loadRecentPages();

    return () => {
      cancelled = true;
    };
  }, [contentLang]);

  useEffect(() => {
    let cancelled = false;

    async function loadSiteInfo() {
      try {
        const info = await wikiApiClient.getSite();

        if (cancelled) {
          return;
        }

        const slug = info.main_page_slug.trim();
        const perLanguage = info.main_pages ?? {};
        setMainPages(perLanguage);

        if (slug) {
          setMainPageSlug(slug);
        }

        // Resolve the home page. On the bare home (no page in the URL): open the main
        // page in the requested language if it exists, otherwise fall back to the
        // language the main page actually exists in (main_page_lang) and switch the
        // session to it — so `/` always lands on the real main page even when the
        // default UI language has no content yet.
        const matched = matchRoute(window.location.pathname);
        if (!routeSlug(matched)) {
          const reqLang = routeLang(matched) ?? defaultLang;
          const langHome = perLanguage[reqLang];

          if (langHome) {
            setActiveSlug(langHome);
          } else if (info.main_page_lang && slug) {
            const canonicalLang = info.main_page_lang;
            setContentLang(canonicalLang);
            setActiveSlug(slug);
            setRoute({ kind: 'reader', slug: null, lang: canonicalLang });
            window.history.replaceState(null, '', `/${canonicalLang}/`);
          } else if (slug) {
            setActiveSlug(slug);
          }
        }
      } catch {
        /* homeSlug stays empty; navigateHome falls back to null-slug reader */
      }
    }

    loadSiteInfo();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const nextRoute = readCurrentRoute();
      setRoute(nextRoute);
      setActiveSlug(routeSlug(nextRoute) ?? homeSlug ?? null);
      // Only routes that carry their own language change contentLang; routes built
      // without one (login, stats, …) preserve the current language.
      if (nextRoute.lang) {
        setContentLang(nextRoute.lang);
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);
  }, [homeSlug]);

  // The route is the source of truth for the UI language: whenever the content
  // language (from the URL's /{lang}/ prefix) changes — via navigation, the language
  // switcher, or browser back/forward — the UI chrome locale follows it.
  useEffect(() => {
    setLocale(contentLang);
  }, [contentLang, setLocale]);

  useEffect(() => {
    if (!activeSlug && homeSlug) {
      setActiveSlug(homeSlug);
    }
  }, [activeSlug, homeSlug]);

  useEffect(() => {
    if (!activeSlug) {
      setActivePage(null);
      setPageGraph(null);
      setPageMissing(null);
      return;
    }

    let cancelled = false;
    const redirectNo = redirectNoRef.current;
    redirectNoRef.current = false;

    async function loadPage(slug: string) {
      setIsLoadingPage(true);
      setReaderError(null);
      setPageMissing(null);

      try {
        const page = await wikiApiClient.getPage(contentLang, slug, { redirectNo });

        if (cancelled) {
          return;
        }

        setActivePage(page);
        document.title = `${page.title} - ${SITE.title}`;

        if (page.redirected_from && !redirectNo) {
          window.history.replaceState(
            null,
            '',
            `/${contentLang}/pages/${encodeURIComponent(page.slug)}`,
          );
        }
      } catch (error) {
        if (!cancelled) {
          setActivePage(null);
          if (error instanceof WikiApiError && error.status === 404) {
            setPageMissing({ slug, lang: contentLang });
          } else {
            setReaderError(errorMessage(error, t('error_load_page')));
          }
        }
      } finally {
        if (!cancelled) {
          setIsLoadingPage(false);
        }
      }
    }

    loadPage(activeSlug);

    return () => {
      cancelled = true;
    };
  }, [activeSlug, contentLang, t]);

  useEffect(() => {
    if (!activeSlug) {
      setPageGraph(null);
      setGraphError(null);
      setIsLoadingGraph(false);
      return;
    }

    let cancelled = false;

    async function loadPageGraph(slug: string) {
      setIsLoadingGraph(true);
      setGraphError(null);
      setPageGraph(null);

      try {
        const graph = await wikiApiClient.getPageGraph(contentLang, slug);

        if (!cancelled) {
          setPageGraph(graph);
        }
      } catch (error) {
        if (!cancelled) {
          setPageGraph(null);
          setGraphError(errorMessage(error, t('error_load_graph')));
        }
      } finally {
        if (!cancelled) {
          setIsLoadingGraph(false);
        }
      }
    }

    loadPageGraph(activeSlug);

    return () => {
      cancelled = true;
    };
  }, [activeSlug, contentLang, t]);

  useEffect(() => {
    const trimmedQuery = query.trim();

    if (!trimmedQuery) {
      setSearchResults(null);
      setSearchError(null);
      setIsSearching(false);
      return;
    }

    let cancelled = false;

    async function searchPages() {
      setIsSearching(true);
      setSearchError(null);

      try {
        const result = await wikiApiClient.searchPages(contentLang, {
          q: trimmedQuery,
          limit: LIMITS.pageLimit,
        });

        if (!cancelled) {
          setSearchResults(result.pages);
        }
      } catch (error) {
        if (!cancelled) {
          setSearchResults(null);
          setSearchError(errorMessage(error, t('error_search')));
        }
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    }

    searchPages();

    return () => {
      cancelled = true;
    };
  }, [query, contentLang, t]);

  return (
    <>
      <TopBar
        query={query}
        onQueryChange={setQuery}
        onNavigateHome={navigateHome}
        onSelectLanguage={selectLanguage}
        onCreatePage={validatedAccount ? () => navigateToRoute({ kind: 'newProposal' }) : undefined}
        personalTools={
          <>
            <NotificationsBell />
            <AuthPanel
              account={validatedAccount}
              canModerate={canModerate}
              pendingProposals={pendingProposals}
              variant="bar"
              onLogin={() => navigateToRoute({ kind: 'login' })}
              onLogout={auth.logout}
              onModeration={() => navigateToRoute({ kind: 'moderation', proposalID: null })}
              onPasswordChange={() => navigateToRoute({ kind: 'changePassword' })}
              onPropose={() => navigateToRoute({ kind: 'newProposal' })}
              onRegister={() => navigateToRoute({ kind: 'register' })}
            />
          </>
        }
      />
      <div className={`site-shell${showGraph ? ' has-meta' : ''}`} data-testid="site-shell">
        <SiteNav
          graphLabel={graphToggleLabel}
          homeSlug={homeSlug}
          graphVisible={showGraph}
          tocSlot={activePage ? <TableOfContents headings={activePage.headings} /> : undefined}
          onAllPages={() => navigateToRoute({ kind: 'all', lang: contentLang })}
          onCategories={() => navigateToRoute({ kind: 'categories' })}
          onMap={() => navigateToRoute({ kind: 'map' })}
          onNavigateHome={navigateHome}
          onRandom={navigateRandomPage}
          onRecentChanges={() => navigateToRoute({ kind: 'recentChanges' })}
          onReports={() => navigateToRoute({ kind: 'reports' })}
          onStats={() => navigateToRoute({ kind: 'stats' })}
          onWatchlist={
            validatedAccount ? () => navigateToRoute({ kind: 'watchedPages' }) : undefined
          }
          onAdminUsers={canSuperadmin ? () => navigateToRoute({ kind: 'adminUsers' }) : undefined}
          onToggleGraph={() => setShowGraph((visible) => !visible)}
        >
          <PageListSidebar
            activeSlug={activePage?.slug ?? activeSlug ?? undefined}
            homeSlug={homeSlug}
            lang={contentLang}
            isLoading={isLoadingRecent}
            isSearching={isSearching}
            pages={sidebarPages}
            query={query}
            searchError={searchError}
            onAllPages={() => navigateToRoute({ kind: 'all', lang: contentLang })}
            onNavigate={navigateTo}
          />
        </SiteNav>

        <main className="reader" data-testid="reader-panel">
          {route.kind === 'login' ? (
            <LoginPage
              onRegisterLink={() => navigateToRoute({ kind: 'register' })}
              onSuccess={() => navigateToRoute({ kind: 'newProposal' })}
            />
          ) : null}

          {route.kind === 'register' ? (
            <RegisterPage
              onLoginLink={() => navigateToRoute({ kind: 'login' })}
              onSuccess={() => navigateToRoute({ kind: 'newProposal' })}
            />
          ) : null}

          {route.kind === 'changePassword' ? (
            <ChangePasswordPage
              onCancel={() =>
                activeSlug
                  ? navigateToRoute({ kind: 'reader', slug: activeSlug, lang: contentLang })
                  : undefined
              }
              onLoginLink={() => navigateToRoute({ kind: 'login' })}
            />
          ) : null}

          {route.kind === 'newProposal' ? (
            <ProposeEditor
              initialSlug={route.slug}
              initialTitle={route.title}
              language={route.language}
              translationGroup={route.translationGroup}
              mode="new"
              onCancel={() =>
                activeSlug
                  ? navigateToRoute({ kind: 'reader', slug: activeSlug, lang: contentLang })
                  : undefined
              }
              onLogin={() => navigateToRoute({ kind: 'login' })}
              onRegister={() => navigateToRoute({ kind: 'register' })}
              onSubmitted={rememberProposal}
            />
          ) : null}

          {route.kind === 'editProposal' ? (
            <ProposeEditor
              isPageLoading={isLoadingPage}
              mode="edit"
              page={activePage}
              onCancel={() =>
                activeSlug
                  ? navigateToRoute({ kind: 'reader', slug: activeSlug, lang: contentLang })
                  : undefined
              }
              onLogin={() => navigateToRoute({ kind: 'login' })}
              onRegister={() => navigateToRoute({ kind: 'register' })}
              onSubmitted={rememberProposal}
            />
          ) : null}

          {route.kind === 'moderation' ? (
            <ModerationDashboard
              lang={contentLang}
              selectedProposalID={route.proposalID}
              onLogin={() => navigateToRoute({ kind: 'login' })}
              onRegister={() => navigateToRoute({ kind: 'register' })}
              onSelectProposal={(proposalID) => navigateToRoute({ kind: 'moderation', proposalID })}
              onAuditLog={() => navigateToRoute({ kind: 'auditLog' })}
            />
          ) : null}

          {route.kind === 'auditLog' ? (
            <AuditLog
              onLogin={() => navigateToRoute({ kind: 'login' })}
              onRegister={() => navigateToRoute({ kind: 'register' })}
            />
          ) : null}

          {route.kind === 'allPages' ? (
            <AllPages
              homeSlug={homeSlug}
              lang={contentLang}
              onNavigate={navigateTo}
              onSelectPrefix={(prefix) =>
                navigateToRoute({ kind: 'all', lang: contentLang, prefix })
              }
            />
          ) : null}

          {route.kind === 'all' ? (
            <AllPages
              homeSlug={homeSlug}
              lang={route.lang ?? contentLang}
              prefix={route.prefix}
              onNavigate={navigateTo}
              onSelectPrefix={(prefix) =>
                navigateToRoute({ kind: 'all', lang: route.lang, prefix })
              }
            />
          ) : null}

          {route.kind === 'categories' ? (
            <CategoryIndex
              lang={contentLang}
              onNavigate={(name) => navigateToRoute({ kind: 'category', name })}
            />
          ) : null}

          {route.kind === 'category' ? (
            <CategoryView lang={contentLang} name={route.name} onNavigate={navigateTo} />
          ) : null}

          {route.kind === 'recentChanges' ? (
            <RecentChanges lang={contentLang} onNavigate={navigateTo} />
          ) : null}

          {route.kind === 'reports' ? <Reports lang={contentLang} onNavigate={navigateTo} /> : null}

          {route.kind === 'stats' ? <Stats /> : null}

          {route.kind === 'watchedPages' ? (
            <WatchedPages
              onNavigate={(slug, lang) => {
                setContentLang(lang);
                navigateTo(slug);
              }}
            />
          ) : null}

          {route.kind === 'reader' ? (
            <ReaderArticle
              activeSlug={activeSlug ?? undefined}
              backlinks={pageGraph?.backlinks ?? []}
              currentLang={contentLang}
              error={readerError}
              fallbackCoverImage={ASSETS.storyCover}
              homeSlug={homeSlug}
              isLoading={isLoadingPage}
              mapPreviewSlot={<MapPreview onNavigate={() => navigateToRoute({ kind: 'map' })} />}
              watchSlot={activePage?.id ? <WatchToggle pageID={activePage.id} /> : undefined}
              outboundIds={pageGraph?.graph.adjacency[pageGraph.center.id] ?? []}
              page={activePage}
              pageMissingSlug={pageMissing?.slug}
              relevant={pageGraph?.relevant ?? []}
              onCreateMissingPage={
                pageMissing
                  ? () => {
                      if (validatedAccount) {
                        navigateToRoute({
                          kind: 'newProposal',
                          slug: pageMissing.slug,
                          title: humanizeSlug(pageMissing.slug),
                        });
                      } else {
                        navigateToRoute({ kind: 'login' });
                      }
                    }
                  : undefined
              }
              onEdit={(page) =>
                navigateToRoute({ kind: 'editProposal', slug: page.slug, lang: contentLang })
              }
              onHistory={(page) =>
                navigateToRoute({ kind: 'history', slug: page.slug, lang: contentLang })
              }
              onInfo={(page) =>
                navigateToRoute({ kind: 'pageInfo', slug: page.slug, lang: contentLang })
              }
              onNavigate={navigateTo}
              onNavigateCategory={(name) => navigateToRoute({ kind: 'category', name })}
              onNavigateLang={navigateLang}
              onNavigateNoRedirect={navigateNoRedirect}
              onProposeMissing={(defaults) => navigateToRoute({ kind: 'newProposal', ...defaults })}
            />
          ) : null}

          {route.kind === 'history' ? (
            <RevisionHistory
              lang={route.lang ?? contentLang}
              slug={route.slug}
              revisionID={route.revisionID}
              currentMarkdown={activePage?.markdown}
              onBack={() => navigateToRoute({ kind: 'reader', slug: route.slug, lang: route.lang })}
              onSelectRevision={(revisionID) =>
                navigateToRoute({ kind: 'history', slug: route.slug, lang: route.lang, revisionID })
              }
            />
          ) : null}

          {route.kind === 'pageInfo' ? (
            <PageInfoView
              lang={route.lang ?? contentLang}
              slug={route.slug}
              onBack={() => navigateToRoute({ kind: 'reader', slug: route.slug, lang: route.lang })}
            />
          ) : null}

          {route.kind === 'map' ? <MapView lang={contentLang} onNavigate={navigateTo} /> : null}

          {route.kind === 'adminUsers' ? <UserAdmin /> : null}
        </main>

        {showGraph ? (
          <GraphPanel
            error={graphError}
            isLoading={isLoadingGraph}
            pageGraph={pageGraph}
            onClose={() => setShowGraph(false)}
            onNavigate={navigateTo}
          />
        ) : null}
      </div>
    </>
  );
}

function recentChangeToPageSummary(change: RecentChange): PageSummary {
  return {
    id: change.page_id,
    slug: change.slug,
    title: change.title,
    language: change.language,
    current_revision: change.revision_id,
    media_urls: [],
    is_stub: false,
    created_at: change.published_at,
    updated_at: change.published_at,
  };
}

function readCurrentRoute(): Route {
  const route = matchRoute(window.location.pathname);
  if (route.kind === 'newProposal') {
    const params = new URLSearchParams(window.location.search);
    return {
      ...route,
      slug: params.get('slug')?.trim() || undefined,
      title: params.get('title')?.trim() || undefined,
    };
  }
  if (route.kind === 'all') {
    const params = new URLSearchParams(window.location.search);
    const prefix = params.get('prefix')?.trim() || undefined;
    return { ...route, prefix };
  }
  return route;
}

function routeSlug(route: Route): string | null {
  if (route.kind === 'reader') {
    return route.slug;
  }

  if (route.kind === 'editProposal') {
    return route.slug;
  }

  if (route.kind === 'history') {
    return route.slug;
  }

  if (route.kind === 'pageInfo') {
    return route.slug;
  }

  return null;
}

// routeLang resolves the content language for a route, defaulting to the base
// language. Used to seed contentLang from the initial/matched route (which always
// carries a lang). To decide whether navigation should *change* contentLang, read
// `route.lang` directly instead — it is undefined for routes built without one, so
// navigating to e.g. /login preserves the current language rather than resetting it.
function routeLang(route: Route): string {
  return route.lang ?? defaultLang;
}

function currentLocationPath(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

function humanizeSlug(slug: string): string {
  return slug.replace(/[-_]/g, ' ');
}

export default AppShell;
